import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { times } from 'lodash';
import { Box, Checkbox, FormHelperText, Typography } from '@mui/material';
import { CheckCircle as CheckedCircle, RadioButtonUnchecked as UncheckedIcon } from '@mui/icons-material';

import TaskItems from '@dyl/blocks/TaskItems';
import withProps from '@dyl/utils/withProps';
import RubricBlock from '@dyl/blocks/RubricBlock';
import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import indexToLetter from '@dyl/utils/indexToLetter';
import AudioAndTranscriptBlock from '@dyl/blocks/AudioAndTranscriptBlock';
import useLocalTaskError from '@dyl/pages/Tasks/edit/useLocalTaskError';

/**
 * @template T
 * @typedef {import('@dyl/pages/Tasks/edit/useTaskField').UseTaskField<T>} UseTaskField
 */

export default withProps(TaskItems, { itemComponent: Item });

function Item({ index }) {
    /** @type {UseTaskField<{ audio_id: string, is_correct_answer: boolean }[]>} */
    const [answers] = useTaskField('items', index, 'answers');

    const radioError = useMemo(() => {
        return !answers.some(a => a.is_correct_answer)
            ? 'Must select one answer as correct.'
            : null;
    }, [answers]);

    const [shouldShowRadioError, setShowRadioError] = useState(false);
    useLocalTaskError(
        radioError && `${index === 0 ? 'Example' : `Item ${index}`}: ${radioError}`,
        () => setShowRadioError(true)
    );

    useEffect(() => {
        if (!shouldShowRadioError) {
            setShowRadioError(false);
        }
    }, [shouldShowRadioError]);

    return (
        <>
            <Typography variant='h6'>
                {index === 0 ? 'Example' : `Item ${index}`}
            </Typography>

            <FormHelperText error={!!shouldShowRadioError}>
                {shouldShowRadioError && radioError}
            </FormHelperText>

            {index === 0 && <RubricBlock />}

            {times(answers.length, answerIndex => (
                <Answer
                    key={answerIndex}
                    itemIndex={index}
                    index={answerIndex}
                />
            ))}
        </>
    );
}

function Answer({ itemIndex, index }) {
    /** @type {UseTaskField<string>} */
    const [audioId, setAudioId, audioError] = useTaskField('items', itemIndex, 'answers', index, 'audio_id');

    /** @type {UseTaskField<boolean>} */
    const [isCorrect] = useTaskField('items', itemIndex, 'answers', index, 'is_correct_answer');

    /** @type {UseTaskField<{ audio_id: string, is_correct_answer: boolean }[]>} */
    const [, setAnswers] = useTaskField('items', itemIndex, 'answers');

    const setAsCorrect = useCallback(() => {
        setAnswers(prev => prev.map((answer, i) => ({
            ...answer,
            is_correct_answer: i === index,
        })));
    }, [index, setAnswers]);

    return (
        <Box display='flex' flexDirection='row' alignItems='flex-start'>
            <Checkbox
                color='primary'
                checked={isCorrect}
                onClick={setAsCorrect}
                icon={<UncheckedIcon />}
                checkedIcon={<CheckedCircle />}
                style={{ marginTop: 8 }}
            />

            <AudioAndTranscriptBlock
                value={audioId}
                onChange={id => setAudioId(id)}
                error={audioError}
                label={`Answer ${indexToLetter(index)}`}
            />
        </Box>
    );
}